import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { entityService, type ListParams } from '@/services/EntityService';
import type { Entity } from '@/types/Entity';
import { formatNomenclatureWithSize } from '@equip/utils';
import {
  buildSuggestOnHandInventoryReportHtml,
  type SuggestOnHandInventoryItem,
  type SuggestOnHandInventoryGrandTotals,
  type SuggestOnHandInventoryTemplateOptions,
} from '@/reports/templates/suggestOnHandInventoryTemplate';

interface TransactionLogAsset {
  assetId: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  transQty: number;
}

interface TransactionLogDetail {
  id: string;
  transactionType: string;
  dateProcessed: string;
  assets: TransactionLogAsset[];
}

interface TransactionLogResponse {
  transactions: TransactionLogDetail[];
}

const listAllEntities = async (
  params: Omit<ListParams, 'limit' | 'offset'>,
): Promise<Entity[]> => {
  return entityService.listAll(params);
};

const parseCsvList = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

/**
 * Normalizes IE Kit filter input to a standardized array of non-empty strings.
 * Handles both array input (from multiselect) and comma-separated string (for backwards compatibility).
 * Returns empty array if input is null/undefined or contains only empty values.
 */
const normalizeIeFilter = (value: unknown): string[] => {
  if (value == null) {
    return [];
  }
  
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.trim());
  }
  
  if (typeof value === 'string') {
    return parseCsvList(value);
  }
  
  console.warn('Unexpected ieFilter type:', typeof value);
  return [];
};

/**
 * Formats an array of strings for display in report headers.
 * Returns null if array is empty, otherwise joins with ', '.
 */
const formatArrayForDisplay = (arr: string[]): string | null => {
  return arr.length > 0 ? arr.join(', ') : null;
};

const coerceCsvInput = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return typeof value === 'string' ? value : '';
};

const matchesAnyToken = (value: string, tokens: string[]): boolean => {
  const lowerValue = value.toLowerCase();
  return tokens.some((token) => lowerValue.includes(token.toLowerCase()));
};

const normalizeLinTokenSet = (tokens: string[]): Set<string> =>
  new Set(tokens.map((token) => token.trim().toUpperCase()).filter(Boolean));

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const hasSizeMarker = (value: string): boolean =>
  /\([^)]*\d|(?:^|\s)\d+(?:\.\d+)?(?:\/\d+)?(?:\s|$)|\b(XXS|XS|S|SM|M|MD|L|LG|XL|XXL|XXXL|NONSIZED)\b/i.test(
    value,
  );

const pickPreferredNomenclature = (current: string | null | undefined, incoming: string): string => {
  const currentValue = (current ?? '').trim();
  const incomingValue = incoming.trim();

  if (!currentValue || currentValue === '—') return incomingValue;
  if (!incomingValue || incomingValue === '—') return currentValue;

  const currentHasSize = hasSizeMarker(currentValue);
  const incomingHasSize = hasSizeMarker(incomingValue);

  if (incomingHasSize && !currentHasSize) return incomingValue;
  if (currentHasSize && !incomingHasSize) return currentValue;

  return incomingValue.length > currentValue.length ? incomingValue : currentValue;
};

const buildItemKey = (lin: string | null | undefined, nsn: string | null | undefined): string =>
  `${lin?.trim().toUpperCase() ?? ''}|${nsn?.trim().toUpperCase() ?? ''}`;

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const buildIeLinSet = async (ieTokens: string[]): Promise<Set<string>> => {
  if (ieTokens.length === 0) return new Set<string>();

  // Avoid hitting the heavy `api_v1.ie_summary` view without `person_id` (can 400/time out).
  // Instead: select IE kits by name -> fetch kit items -> fetch catalogs -> return LIN set.

  const ieKits = await listAllEntities(
    { type: 'ie_kit', order: 'id.asc', filters: { select: 'id,display_name' } },
  );

  const matchingKitIds = new Set(
    ieKits
      .filter((kit) => matchesAnyToken(kit.display_name ?? '', ieTokens))
      .map((kit) => kit.id),
  );

  if (matchingKitIds.size === 0) {
    return new Set<string>();
  }

  const ieKitItems = await listAllEntities(
    { type: 'ie_kit_item', order: 'id.asc', filters: { select: 'entity_data' } },
  );

  const catalogIds = Array.from(
    new Set(
      ieKitItems
        .filter((item) => {
          const kitId = coerceString(item.entity_data?.ie_kit_id);
          return kitId ? matchingKitIds.has(kitId) : false;
        })
        .map((item) => coerceString(item.entity_data?.catalog_id))
        .filter((id): id is string => Boolean(id)),
    ),
  );

  if (catalogIds.length === 0) {
    return new Set<string>();
  }

  const linSet = new Set<string>();

  for (const batchIds of chunk(catalogIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      const lin = coerceString(entityData.lin);
      if (lin) {
        linSet.add(lin.trim().toUpperCase());
      }
    }
  }

  return linSet;
};

export const runSuggestOnHandInventoryReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const startDateRaw = coerceString(params.startDate);
  const endDateRaw = coerceString(params.endDate);
  const linFilterRaw = coerceCsvInput(params.linFilter);
  // Normalize IE Kit filter input (handles both array from multiselect and CSV string)
  const ieTokens = normalizeIeFilter(params.ieFilter);

  const linTokens = parseCsvList(linFilterRaw);
  const linSet = normalizeLinTokenSet(linTokens);
  const ieLinSet = await buildIeLinSet(ieTokens);

  // TODO: Consider implementing pagination or explaining the 5000 limit to users
  // If results exceed this limit, they will be silently truncated
  const query = new URLSearchParams({ limit: '5000', types: 'ISSUE_OUT', source: 'report' });
  if (startDateRaw) {
    query.set('from', startDateRaw);
  }
  if (endDateRaw) {
    query.set('to', endDateRaw);
  }

  const response = await httpClient.get<TransactionLogResponse>(
    `/api/v1/transactions/log?${query.toString()}`,
  );
  const transactions = response.transactions ?? [];

  const byKey = new Map<
    string,
    {
      lin: string | null;
      nsn: string | null;
      nomenclature: string;
      year: number;
      month: number;
      qtyIssued: number;
    }
  >();
  const canonicalNomenByItemKey = new Map<string, string>();

  for (const transaction of transactions) {
    const processedDate = new Date(transaction.dateProcessed);
    if (Number.isNaN(processedDate.getTime())) continue;

    const year = processedDate.getUTCFullYear();
    const month = processedDate.getUTCMonth(); // 0–11

    for (const asset of transaction.assets ?? []) {
      const lin = asset.lin === '—' ? null : asset.lin;
      const nsn = asset.nsn === '—' ? null : asset.nsn;
      const nomen = asset.nomenclature || '—';
      const itemKey = buildItemKey(lin, nsn);

      canonicalNomenByItemKey.set(
        itemKey,
        pickPreferredNomenclature(canonicalNomenByItemKey.get(itemKey), nomen),
      );

      if (linSet.size > 0) {
        if (!lin) continue;
        if (!linSet.has(lin.trim().toUpperCase())) continue;
      }

      if (ieLinSet.size > 0) {
        if (!lin) continue;
        if (!ieLinSet.has(lin.trim().toUpperCase())) continue;
      }

      const qty =
        typeof asset.transQty === 'number' && Number.isFinite(asset.transQty) ? asset.transQty : 1;

      // Group by lin|nsn|year|month without nomen so that the same NSN is
      // consolidated across months regardless of how the nomenclature was
      // stored in historical transactions (some had size embedded, e.g.
      // "CAP, PATROL OCP 7 1/4"; newer ones have the stripped clean name).
      const key = `${lin ?? ''}|${nsn ?? ''}|${year}|${month}`;

      let entry = byKey.get(key);
      if (!entry) {
        entry = { lin, nsn, nomenclature: nomen, year, month, qtyIssued: 0 };
        byKey.set(key, entry);
      }
      entry.qtyIssued += qty;
    }
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Lookup catalog unit prices by LIN
  const uniqueLins = Array.from(
    new Set(
      Array.from(byKey.values())
        .map((e) => e.lin?.trim().toUpperCase())
        .filter((lin): lin is string => Boolean(lin)),
    ),
  );

  const priceByLin = new Map<string, number>();
  const catalogNomenByItemKey = new Map<string, string>();
  const catalogSizedNomenByNsn = new Map<string, string>();
  if (uniqueLins.length > 0) {
    // First pass: fetch parent/standalone catalog entries (parent_id IS NULL).
    // These reliably carry unit_price and avoid size-variant rows that lack pricing.
    for (const batchLins of chunk(uniqueLins, 50)) {
      const linFilter = batchLins.length === 1
        ? `eq.${batchLins[0]}`
        : `in.(${batchLins.join(',')})`;
      const catalogs = await entityService.list({
        type: 'catalog',
        limit: batchLins.length * 2,
        order: 'id.asc',
        filters: {
          'entity_data->>lin': linFilter,
          'entity_data->>parent_id': 'is.null',
          select: 'id,display_name,entity_data',
        },
      });
      for (const cat of catalogs) {
        const entityData = (cat.entity_data ?? {}) as Record<string, unknown>;
        const lin = coerceString(entityData.lin)?.trim().toUpperCase();
        const nsn = coerceString(entityData.nsn)?.trim().toUpperCase();
        if (!lin) continue;

        if (nsn) {
          const baseNomenclature =
            coerceString(cat.display_name) ??
            coerceString(entityData.name);
          const size = coerceString(entityData.size);
          const sizedNomenclature = baseNomenclature
            ? formatNomenclatureWithSize(baseNomenclature, size)
            : null;
          if (sizedNomenclature) {
            const itemKey = buildItemKey(lin, nsn);
            catalogNomenByItemKey.set(
              itemKey,
              pickPreferredNomenclature(catalogNomenByItemKey.get(itemKey), sizedNomenclature),
            );
            if (size) {
              catalogSizedNomenByNsn.set(
                nsn,
                pickPreferredNomenclature(catalogSizedNomenByNsn.get(nsn), sizedNomenclature),
              );
            }
          }
        }

        if (priceByLin.has(lin)) continue;
        const rawPrice = entityData.unit_price;
        const price = typeof rawPrice === 'number' ? rawPrice
          : typeof rawPrice === 'string' ? Number.parseFloat(rawPrice)
          : null;
        if (price !== null && Number.isFinite(price)) {
          priceByLin.set(lin, price);
        }
      }
    }

    // Second pass: for any LINs still missing prices, try all catalog entries.
    const missingLins = uniqueLins.filter((lin) => !priceByLin.has(lin));
    for (const batchLins of chunk(missingLins, 50)) {
      const linFilter = batchLins.length === 1
        ? `eq.${batchLins[0]}`
        : `in.(${batchLins.join(',')})`;
      const catalogs = await entityService.list({
        type: 'catalog',
        limit: batchLins.length * 10,
        order: 'id.asc',
        filters: {
          'entity_data->>lin': linFilter,
          select: 'id,display_name,entity_data',
        },
      });
      for (const cat of catalogs) {
        const entityData = (cat.entity_data ?? {}) as Record<string, unknown>;
        const lin = coerceString(entityData.lin)?.trim().toUpperCase();
        const nsn = coerceString(entityData.nsn)?.trim().toUpperCase();
        if (!lin) continue;

        if (nsn) {
          const baseNomenclature =
            coerceString(cat.display_name) ??
            coerceString(entityData.name);
          const size = coerceString(entityData.size);
          const sizedNomenclature = baseNomenclature
            ? formatNomenclatureWithSize(baseNomenclature, size)
            : null;
          if (sizedNomenclature) {
            const itemKey = buildItemKey(lin, nsn);
            catalogNomenByItemKey.set(
              itemKey,
              pickPreferredNomenclature(catalogNomenByItemKey.get(itemKey), sizedNomenclature),
            );
            if (size) {
              catalogSizedNomenByNsn.set(
                nsn,
                pickPreferredNomenclature(catalogSizedNomenByNsn.get(nsn), sizedNomenclature),
              );
            }
          }
        }

        if (priceByLin.has(lin)) continue;
        const rawPrice = entityData.unit_price;
        const price = typeof rawPrice === 'number' ? rawPrice
          : typeof rawPrice === 'string' ? Number.parseFloat(rawPrice)
          : null;
        if (price !== null && Number.isFinite(price)) {
          priceByLin.set(lin, price);
        }
      }
    }

    const uniqueNsns = Array.from(
      new Set(
        Array.from(byKey.values())
          .map((e) => e.nsn?.trim().toUpperCase())
          .filter((nsn): nsn is string => Boolean(nsn)),
      ),
    );

    for (const batchNsns of chunk(uniqueNsns, 50)) {
      const nsnFilter = batchNsns.length === 1
        ? `eq.${batchNsns[0]}`
        : `in.(${batchNsns.join(',')})`;
      const catalogs = await entityService.list({
        type: 'catalog',
        limit: batchNsns.length * 10,
        order: 'id.asc',
        filters: {
          'entity_data->>nsn': nsnFilter,
          select: 'id,display_name,entity_data',
        },
      });
      for (const cat of catalogs) {
        const entityData = (cat.entity_data ?? {}) as Record<string, unknown>;
        const lin = coerceString(entityData.lin)?.trim().toUpperCase();
        const nsn = coerceString(entityData.nsn)?.trim().toUpperCase();
        if (!lin || !nsn) continue;

        const baseNomenclature =
          coerceString(cat.display_name) ??
          coerceString(entityData.name);
        const size = coerceString(entityData.size);
        const sizedNomenclature = baseNomenclature
          ? formatNomenclatureWithSize(baseNomenclature, size)
          : null;
        if (sizedNomenclature) {
          const itemKey = buildItemKey(lin, nsn);
          catalogNomenByItemKey.set(
            itemKey,
            pickPreferredNomenclature(catalogNomenByItemKey.get(itemKey), sizedNomenclature),
          );
          if (size) {
            catalogSizedNomenByNsn.set(
              nsn,
              pickPreferredNomenclature(catalogSizedNomenByNsn.get(nsn), sizedNomenclature),
            );
          }
        }
      }
    }
  }

  let grandTotalQty = 0;
  let grandTotalCost: number | null = null;

  const items: SuggestOnHandInventoryItem[] = Array.from(byKey.values())
    .map((entry) => {
      const linUpper = entry.lin?.trim().toUpperCase() ?? '';
      const itemKey = buildItemKey(entry.lin, entry.nsn);
      const nsnUpper = entry.nsn?.trim().toUpperCase() ?? '';
      const unitPrice = priceByLin.get(linUpper) ?? null;
      const linTotalCost = unitPrice !== null && entry.qtyIssued > 0
        ? unitPrice * entry.qtyIssued
        : null;

      grandTotalQty += entry.qtyIssued;
      if (linTotalCost !== null) {
        grandTotalCost = (grandTotalCost ?? 0) + linTotalCost;
      }

      // Keep nomenclature stable across months for the same LIN/NSN while
      // preferring size-inclusive labels when historical rows are mixed.
      const nomenclature =
        catalogNomenByItemKey.get(itemKey) ??
        (nsnUpper ? catalogSizedNomenByNsn.get(nsnUpper) : undefined) ??
        canonicalNomenByItemKey.get(itemKey) ??
        entry.nomenclature;

      return {
        lin: entry.lin,
        nsn: entry.nsn,
        nomenclature,
        qtyIssued: entry.qtyIssued,
        unitPrice,
        linTotalCost,
        year: entry.year,
        monthLabel: monthNames[entry.month] ?? '',
      };
    })
    .sort((a, b) => {
      const yearDiff = (a.year ?? 0) - (b.year ?? 0);
      if (yearDiff !== 0) return yearDiff;
      const monthDiff =
        monthNames.indexOf(a.monthLabel ?? '') - monthNames.indexOf(b.monthLabel ?? '');
      if (monthDiff !== 0) return monthDiff;
      return (a.lin ?? '').localeCompare(b.lin ?? '');
    });

  const grandTotals: SuggestOnHandInventoryGrandTotals | null = items.length > 0
    ? { totalQtyIssued: grandTotalQty, totalCost: grandTotalCost }
    : null;

  const ieKitLabel = (params.ieKitLabel as string | undefined) ?? 'IE Kit';

  const options: SuggestOnHandInventoryTemplateOptions = {
    generatedAt: new Date(),
    header: {
      startDate: startDateRaw,
      endDate: endDateRaw,
      linFilter: linFilterRaw.trim() || null,
      ieLabel: formatArrayForDisplay(ieTokens),
    },
    items,
    grandTotals,
    ieKitLabel,
  };

  const html = buildSuggestOnHandInventoryReportHtml(options);
  outputReport(html, 'suggest-on-hand-inventory-report.html', getOutputFormat(params));
};
